import { filter, find, forEach, get, shallowEqual, isEqual } from 'lodash';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useBookOrder } from 'store/books/selectors';

/**
 * Get the hidden books based on the current class and organization permissions.
 *
 * Returns an object with the following properties:
 *   - hiddenBookGroup: an array of book group objects that are hidden.
 *   - hiddenBookGroupBookId: an array of ids of the hidden books in the book group.
 *   - hiddenBook: an array of book objects that are hidden.
 *   - hiddenBookId: an array of ids of the hidden books.
 *   - nonAssignableBookIds: an array of ids of the books that are non-assignable.
 */
const useBookCollection = () => {
  const bookOrder = useSelector(state => state.bookOrder, shallowEqual);
  const booksAllList = useSelector(state => state.booksAllList, shallowEqual);
  const currentClass = useSelector(state => state.currentClass, shallowEqual);
  const orderBook = useBookOrder();

  const archived_group = useMemo(() => currentClass ? currentClass.archived_group || [] : [], [currentClass]);
  const books_group = useMemo(() => currentClass ? currentClass.books_group || [] :  [], [currentClass]);
  const active_group = useMemo(() => books_group.filter((data) => !archived_group.includes(data)), [books_group, archived_group]);
  const multi_group_books = useMemo(() => {
    const book_data = {};
    bookOrder.forEach(book => {
      book.order.forEach(book_id => {
        if (book_data[book_id]) {
          book_data[book_id] = {
            count: book_data[book_id].count + 1,
            group: [...book_data[book_id].group, book.key],
          };
        } else {
          book_data[book_id] = {
            count: 1,
            group: [book.key],
          };
        }
      })
    })
    const response = {};
    Object.entries(book_data).forEach(([bookId, value]) => {
      if (value.count > 1) {
        response[bookId] = value;
      }
    });
    return response
  }, [bookOrder])

  const hiddenOrderedBook = useMemo(() => {
    return bookOrder
      .map((k) => {
        if (archived_group.find((f) => f == k.key)) {
          const result = [
            ...k.order.map((p) => find(booksAllList, (s) => s.id == p)).filter(Boolean)
          ];
          return {
            ...k,
            books: result,
          };
        }
        return null;
      })
      .filter(Boolean);
  }, [bookOrder, booksAllList, currentClass]);

  const hiddenBookGroupBookId = useMemo(() => {
    const response = hiddenOrderedBook.map((b) => b.books.map((p) => Number(p.id))).flat();
    const result = response.filter((data) => {
      const is_multi_group = multi_group_books[data];
      if(is_multi_group){
        return !active_group.some((group) => is_multi_group.group.includes(group));
      }
      return data;
    });
    return result;
  }, [hiddenOrderedBook, multi_group_books,active_group]);

  const hiddenBook = useMemo(() => {
    return get(currentClass, 'hide_books', []);
  }, [currentClass]);
  

  const hiddenBookId = useMemo(() => {
    const response = hiddenBook.map((data) => Number(data?.bookId)) || [];
    const result = response.filter((data) => {
        const is_multi_group = multi_group_books[data];
        if(is_multi_group){
          return !active_group.some((group) => is_multi_group.group.includes(group));
        }
        return data;
    });
    return result;
  }, [hiddenBook, multi_group_books,active_group]);

  const nonAssignableBookIds = useMemo(() => {
    return filter(booksAllList, (book) => book && !!book.onlyTeacher)?.map((book) => Number(book.id));
  }, [booksAllList]);

   const getAllMainBookIds = useMemo(() => {
    try{
      return orderBook.map((bookGroup) => bookGroup.order).flat() || [];
    }catch(error){
      console.log(error);
    }
  },[orderBook])

 


  return useMemo(() => ({
    hiddenBookGroup: hiddenOrderedBook || [],
    hiddenBookGroupBookId: hiddenBookGroupBookId || [],
    hiddenBook: hiddenBook || [],
    hiddenBookId: hiddenBookId || [],
    nonAssignableBookIds: nonAssignableBookIds || [],
    getAllMainBookIds: getAllMainBookIds || [],
    active_group: active_group || [],
  }), [
    hiddenOrderedBook,
    hiddenBookGroupBookId,
    hiddenBook,
    hiddenBookId,
    nonAssignableBookIds,
    getAllMainBookIds,
    active_group,
  ]);
};

export default useBookCollection;
